import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { ButtonToolbar, ButtonGroup, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isEqual as _isEqual } from 'lodash';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';

import EntityPropsUpdater from 'component/EntityPropsUpdater';

import Data from 'components/Data';

import CmsCanvas from 'components/cms/CmsCanvas';

import may from 'js/security';

class PageCanvas extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    const { entity, user } = props;

    const mayEdit = may('ROLE_CMS_PAGES', user.roles);

    this.state = {
      ...this.state,
      history: [JSON.parse(JSON.stringify(entity.processedData.content))],
      historyIndex: 0,
      editMode: mayEdit,
      effectsEnabled: true,
      showTreeViewSlideout: false,
      showQuickPageBuilder: false,
    };
  }

  componentDidUpdate = prevProps => {
    const { pageDraft } = this.props;

    if (
      !pageDraft.pending &&
      !pageDraft.hasError &&
      pageDraft.data &&
      pageDraft.data.content &&
      prevProps.pageDraft.pending
    ) {
      this.updateEntity('content', pageDraft.data.content, [], () => {
        const { entity } = this.props;

        this.setState({ history: [JSON.parse(JSON.stringify(entity.processedData.content))] });
      });
    }
  };

  componentWillUnmount = () => {
    clearTimeout(this.saveDraftTimeout);
  };

  addToHistory = () => {
    const { entity } = this.props;
    const { history, historyIndex } = this.state;

    const currentHistoryEntry = history[historyIndex];

    if (!_isEqual(JSON.stringify(entity.processedData.content), JSON.stringify(currentHistoryEntry))) {
      const newHistory = history.slice();

      if (historyIndex < history.length - 1) {
        const deleteCount = history.length - 1 - historyIndex;
        newHistory.splice(-1 * deleteCount, deleteCount);
      }

      newHistory.push(JSON.parse(JSON.stringify(entity.processedData.content)));

      if (newHistory.length > 50) {
        newHistory.splice(0, 1);
      }

      this.setState({ history: newHistory, historyIndex: newHistory.length - 1 }, this.saveDraft);
    }
  };

  undo = () => {
    this.setState(
      prevState => ({ historyIndex: prevState.historyIndex - 1 }),
      () => {
        const { history, historyIndex } = this.state;

        this.updateEntity('content', history[historyIndex], [], this.saveDraft);
      }
    );
  };

  redo = () => {
    this.setState(
      prevState => ({ historyIndex: prevState.historyIndex + 1 }),
      () => {
        const { history, historyIndex } = this.state;

        this.updateEntity('content', history[historyIndex], [], this.saveDraft);
      }
    );
  };

  toggleEditMode = () => {
    this.setState(prevState => ({ editMode: !prevState.editMode }));
  };

  toggleEffects = () => {
    this.setState(prevState => ({ effectsEnabled: !prevState.effectsEnabled }));
  };

  showTreeViewSlideout = () => {
    this.setState({ showTreeViewSlideout: true, showQuickPageBuilder: false });
  };

  closeTreeViewSlideout = () => {
    this.setState({ showTreeViewSlideout: false, showQuickPageBuilder: false });
  };

  showQuickPageBuilder = () => {
    this.setState({ showQuickPageBuilder: true, showTreeViewSlideout: false });
  };

  closeQuickPageBuilder = () => {
    this.setState({ showQuickPageBuilder: false, showTreeViewSlideout: false });
  };

  saveDraft = () => {
    this.saveDraftTimeout = setTimeout(this.doSaveDraft, 1000);
  };

  doSaveDraft = () => {
    const { entity, pageDraft, createDraft, updateDraft } = this.props;
    const { history, historyIndex } = this.state;

    if (pageDraft.data && pageDraft.data.content) {
      updateDraft(entity.getId(), { content: history[historyIndex] });
    } else {
      createDraft(entity.getId(), { content: history[historyIndex] });
    }
  };

  render() {
    const {
      intl: { messages },
      entity,
      changeEntity,
      disabled,
      pageDraft,
      formStatusNew,
      formStatusEdit,
      user,
    } = this.props;
    const { history, historyIndex, editMode, effectsEnabled, showTreeViewSlideout, showQuickPageBuilder } = this.state;

    const undoDisabled = historyIndex === 0;
    const redoDisabled = historyIndex + 1 === history.length;

    const editModeDisabled = disabled || !may('ROLE_CMS_PAGES', user.roles);

    return (
      <div>
        <Data dataName="pageDraft" url={`/api/pages/${entity.getId()}/drafts.json`} data={pageDraft} />

        <ButtonToolbar className="p-l-20 p-t-10 p-b-10 p-r-20" bsSize="lg">
          <ButtonGroup>
            <Button onClick={this.undo} disabled={editModeDisabled || undoDisabled}>
              <i className="fal fa-undo" />
            </Button>
            <Button onClick={this.redo} disabled={editModeDisabled || redoDisabled}>
              <i className="fal fa-redo" />
            </Button>
          </ButtonGroup>

          <ButtonGroup>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{messages.show_your_page_structure_and_responsive_visibility}</Tooltip>}
            >
              <Button
                onClick={showTreeViewSlideout ? this.closeTreeViewSlideout : this.showTreeViewSlideout}
                active={showTreeViewSlideout}
                disabled={disabled}
              >
                <i className="fal fa-folder-tree" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>

          <ButtonGroup>
            <OverlayTrigger placement="top" overlay={<Tooltip>{messages.toggle_edit_mode_for_page_preview}</Tooltip>}>
              <Button
                onClick={this.toggleEditMode}
                disabled={editModeDisabled}
                active={editMode}
                bsStyle={editMode ? 'info' : 'default'}
              >
                <i className="fal fa-pen-square" /> {messages.edit_mode}
              </Button>
            </OverlayTrigger>
          </ButtonGroup>

          <ButtonGroup>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{messages.toggle_animation_and_parallax_effects}</Tooltip>}
            >
              <Button
                onClick={this.toggleEffects}
                disabled={editModeDisabled}
                active={effectsEnabled}
                bsStyle={effectsEnabled ? 'info' : 'default'}
              >
                <i className="fal fa-sparkles" /> {messages.effects_active}
              </Button>
            </OverlayTrigger>
          </ButtonGroup>

          <ButtonGroup>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{messages.use_quick_page_builder_to_create_content_faster}</Tooltip>}
            >
              <Button
                onClick={showQuickPageBuilder ? this.closeQuickPageBuilder : this.showQuickPageBuilder}
                active={showQuickPageBuilder}
                disabled={editModeDisabled}
              >
                <i className="fal fa-book-spells" />
              </Button>
            </OverlayTrigger>
          </ButtonGroup>

          <ButtonGroup>
            <Button className="no-border">
              <i className="fal fa-pencil-ruler m-r-10" />
              {pageDraft.pending ? (
                '...'
              ) : (
                <>
                  {(formStatusNew.pending || formStatusEdit.pending) && messages.saving_draft + ' ...'}
                  {!formStatusNew.pending && !formStatusEdit.pending && pageDraft.data && messages.draft_saved}
                </>
              )}
            </Button>
          </ButtonGroup>
        </ButtonToolbar>

        {!pageDraft.pending && (
          <CmsCanvas
            editMode={!editModeDisabled && editMode}
            effectsEnabled={effectsEnabled}
            entity={entity}
            changeEntity={changeEntity}
            pageId={entity.getId()}
            onSettingsClose={this.addToHistory}
            onElementAdded={this.addToHistory}
            onElementReordered={this.addToHistory}
            onElementRemoved={this.addToHistory}
            showTreeViewSlideout={showTreeViewSlideout}
            closeTreeViewSlideout={this.closeTreeViewSlideout}
            showQuickPageBuilder={showQuickPageBuilder}
            closeQuickPageBuilder={this.closeQuickPageBuilder}
          />
        )}
      </div>
    );
  }
}
PageCanvas.defaultProps = {
  disabled: false,
};

const mapStateToProps = state => {
  return {
    pageDraft: state.data.pageDraft,
    formStatusNew: state.formStatus.pageDraftNew,
    formStatusEdit: state.formStatus.pageDraftEdit,
    user: state.login.user,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    createDraft: (pageId, args) => {
      formStatusDispatchHelper('pageDraftNew', `/api/pages/${pageId}/drafts.json`, args, 'post', dispatch);
    },
    updateDraft: (pageId, args) => {
      formStatusDispatchHelper('pageDraftEdit', `/api/pages/${pageId}/drafts.json`, args, 'put', dispatch);
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(PageCanvas)
);
